import { lazy, Suspense } from 'react';
import { AuthGuard } from 'src/auth/guard';
import { Outlet, Navigate } from 'react-router-dom';
import DashboardLayout from 'src/layouts/dashboard';

import { paths } from 'src/routes/paths';

import { LoadingScreen } from 'src/components/loading-screen';

// ----------------------------------------------------------------------

import CompanyWorkspaceGuard from './company-workspace-guard';

const DocumentsSignaturesPage = lazy(() => import('@heyjob-dash/shared/features/company-management/pages/DocumentsSignaturesPage'));
const RequestsPage = lazy(() => import('@heyjob-dash/shared/features/company-management/pages/RequestsPage'));
const HolidayPlanPage = lazy(() => import('@heyjob-dash/shared/features/company-management/pages/HolidayPlanPage'));
const ClockingsPage = lazy(() => import('@heyjob-dash/shared/features/company-management/pages/ClockingsPage'));
const HolidayPlanConfigPage = lazy(() => import('@heyjob-dash/shared/features/company-management/pages/config/holiday-plan/HolidayPlanConfigPage'));
const ProjectsConfigPage = lazy(() => import('@heyjob-dash/shared/features/company-management/pages/config/projects/ProjectsConfigPage'));
// OVERVIEW
const IndexPage = lazy(() => import('src/pages/HomePage'));
const CompaniesPage = lazy(() => import('../../pages/company/CompaniesPage'));
const CompanyPage = lazy(() => import('../../pages/company/CompanyPage'));
const CompanyOnboardingPage = lazy(() => import('../../pages/company/CompanyOnboardingPage'));
const OnboardingConfigPage = lazy(() => import('../../pages/OnboardingConfigPage'));
const StudioTemplatesPage = lazy(() => import('../../pages/StudioTemplatesPage'));
const HiringPage = lazy(() => import('src/pages/HiringPage'));
const TerminationsPage = lazy(() => import('src/pages/TerminationsPage'));
const LettersPage = lazy(() => import('src/pages/LettersPage'));
const HelpDeskPage = lazy(() => import('src/pages/HelpDeskPage'));
const NewsPage = lazy(() => import('src/pages/NewsPage'));
const CreateNewsPage = lazy(() => import('src/pages/CreateNewsPage'));
const PopupNoticePage = lazy(() => import('../../pages/PopupNoticePage'));
const FAQLearningPage = lazy(() => import('src/pages/FAQLearningPage'));
const CompanyExternalLinksPage = lazy(() => import('../../pages/CompanyExternalLinksPage'));
const EmployeeExternalLinksPage = lazy(() => import('../../pages/EmployeeExternalLinksPage'));
const UploadDocumentsPage = lazy(() => import('src/pages/UploadDocumentiPage'));
const UploadBustePagaPage = lazy(() => import('src/pages/UploadBustePagaPage'));
const CedoliniImportatiPage = lazy(() => import('src/pages/CedoliniImportatiPage'));

const EmployeesPage = lazy(() => import('src/pages/employee/AnagrafichePage'));
const CompanyAccountPage = lazy(() => import('src/pages/CompanyAccountPage'));
const CompanyConfigPage = lazy(() => import('src/pages/CompanyConfigPage'));
const TimesheetConfigPage = lazy(() => import('src/pages/company/config/timesheet/TimesheetConfigPage'));
const ExpenseReportsConfigPage = lazy(() => import('src/pages/company/config/expense-reports/ExpenseReportsConfigPage'));
const SupervisorsConfigPage = lazy(() => import('src/pages/company/config/supervisors/SupervisorsConfigPage'));
const DocumentTemplatesPage = lazy(() => import('src/pages/company/config/document-templates/DocumentTemplatesPage'));
const EmployeeCustomFieldsConfigPage = lazy(() => import('src/pages/company/config/custom-fields/EmployeeCustomFieldsConfigPage'));
const AreasConfigPage = lazy(() => import('src/pages/company/config/areas/AreasConfigPage'));
const CreateCompanyPage = lazy(() => import('src/pages/company/CreateCompanyPage'));
const CompanyPayrollDataPage = lazy(() => import('src/pages/CompanyPayrollDataPage'));
const CompanyClockingPlacesPage = lazy(() => import('../../pages/CompanyClockingPlacesPage'));
const CreateAnagraficaPage = lazy(() =>
  import('src/pages/employee/CreateAnagraficaPage')
);
const TimesheetPage = lazy(() => import('src/pages/timesheet/TimesheetPage'));
const DocumentsPage = lazy(() => import('src/pages/DocumentsPage'));
const StatistichePage = lazy(() => import('src/pages/StatistichePage'));
const AutoPage = lazy(() => import('src/pages/auto/AutoPage'));
const ExpenseReportsPage = lazy(() => import('src/pages/ExpenseReportsPage'));
const ScadenzarioPage = lazy(() => import('src/pages/ScadenzarioPage'));
const PayTransparencyPage = lazy(() => import('src/pages/company/PayTransparencyPage'));
const PayTransparencyDetailPage = lazy(() => import('src/pages/company/PayTransparencyDetailPage'));
const ConfigPage = lazy(() => import('src/pages/ConfigPage'));
const ImportPage = lazy(() => import('src/pages/ImportPage'));
const TimesheetCategoryDetail = lazy(() => import('src/pages/TimesheetCategoryDetail'));
const PayrollAccountPage = lazy(() => import('src/pages/PayrollAccountPage'));

const EmployeePage = lazy(() => import('src/pages/employee/AnagraficaPage'));
const EmployeesPayrollDataReportPage = lazy(() => import('src/pages/report/EmployeePayrollDataReport'));
const EmployeeDocumentsReport = lazy(() => import('src/pages/report/EmployeeDocumentsReport'));
const ConsultantsPage = lazy(() => import('src/pages/ConsultantsPage'));
const ManagersPage = lazy(() => import('../../pages/manager/ManagersPage'));
const ConsultantDetail = lazy(() => import('src/pages/ConsultantDetail'));
const ManagerPage = lazy(() => import('../../pages/manager/ManagerPage'));
const MonthlyTimesheetPage = lazy(() =>
  import('src/pages/timesheet/MonthlyTimesheetPage')
);
const TimeProfileDetail = lazy(() => import('src/pages/TimeProfileDetail'));
const ImportTypeDetail = lazy(() => import('src/pages/ImportTypeDetail'));
const TimesheetExtraDetail = lazy(() => import('src/pages/TimesheetExtraDetail'));
const ImportDataDetail = lazy(() => import('../../pages/ImportDataDetail'));
const ZucchettiApiPage = lazy(() => import('src/pages/ZucchettiApiPage'));
const BPointPage = lazy(() => import('src/pages/BPointPage'));
const ZucchettiExportPage = lazy(() => import('src/pages/ZucchettiExportPage'));
const ZucchettiPreviewPage = lazy(() => import('src/pages/ZucchettiPreviewPage'));
const ApiKeysPage = lazy(() => import('src/pages/ApiKeysPage'));
const ClockingConnectorPage = lazy(() => import('src/pages/ClockingConnectorPage'));

const AccountPage = lazy(() => import('src/pages/AccountPage'));

// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: '',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen/>}>
            <Outlet/>
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage/>, index: true },
      { path: 'companies', element: <CompaniesPage/> },
      { path: 'companies/new', element: <CreateCompanyPage/> },
      { path: 'companies/:company_id', element: <CompanyPage/> },
      { path: 'companies/:company_id/onboarding', element: <CompanyOnboardingPage/> },
      { path: 'hiring', element: <HiringPage/> },
      { path: 'terminations', element: <TerminationsPage/> },
      { path: 'letters', element: <LettersPage/> },
      { path: 'help-desk', element: <HelpDeskPage/> },
      { path: 'news', element: <NewsPage/> },
      { path: 'news/new', element: <CreateNewsPage/> },
      { path: 'news/:news_id', element: <CreateNewsPage/> },
      { path: 'popup-notices', element: <PopupNoticePage/> },
      { path: 'faq-e-learning', element: <FAQLearningPage/> },
      { path: 'external-links', element: <CompanyExternalLinksPage/> },
      { path: 'employee-external-links', element: <EmployeeExternalLinksPage/> },
      { path: 'upload-documents', element: <UploadDocumentsPage/> },
      { path: 'upload-payslips', element: <UploadBustePagaPage/> },
      { path: 'config', element: <ConfigPage/> },
      { path: 'config/onboarding', element: <OnboardingConfigPage/> },
      { path: 'config/studio-templates', element: <StudioTemplatesPage/> },
      { path: 'import', element: <ImportPage/> },
      { path: 'consultants', element: <ConsultantsPage/> },
      { path: 'consultants/:consultant_email', element: <ConsultantDetail/> },
      { path: 'managers', element: <ManagersPage/> },
      { path: 'managers/:manager_id', element: <ManagerPage/> },

      { path: 'account', element: <AccountPage/> },

      { path: 'payroll', element: <PayrollAccountPage/> },
      { path: 'config/timesheet-categories/:short_code', element: <TimesheetCategoryDetail/> },
      { path: 'config/time-profiles/:time_profile_id', element: <TimeProfileDetail/> },
      { path: 'config/timesheet-extra/:extra_id', element: <TimesheetExtraDetail/> },

      { path: 'config/import-types/:import_type_id', element: <ImportTypeDetail/> },

      { path: 'import/:type/:import_id', element: <ImportDataDetail/> },

      { path: 'company/employees', element: <EmployeesPage/> },
      { path: 'company/employees/new', element: <CreateAnagraficaPage/> },
      { path: 'company/employees/:employee_id', element: <EmployeePage/> },
      { path: 'company/report/employee-payroll-data', element: <EmployeesPayrollDataReportPage/> },
      { path: 'company/report/employee-documents', element: <EmployeeDocumentsReport/> },
      { path: 'company/timesheet', element: <TimesheetPage/> },
      { path: 'company/timesheet/:year/:month', element: <TimesheetPage/> },
      {
        path: 'company/timesheet/:year/:month/:employee_id',
        element: <MonthlyTimesheetPage/>
      },
      { path: 'company/pay-transparency', element: <PayTransparencyPage/> },
      { path: 'company/pay-transparency/:reqId', element: <PayTransparencyDetailPage/> },
      { path: 'company/documents-signatures', element: <CompanyWorkspaceGuard><DocumentsSignaturesPage/></CompanyWorkspaceGuard> },
      { path: 'company/requests', element: <CompanyWorkspaceGuard feature="feature_requests"><RequestsPage/></CompanyWorkspaceGuard> },
      { path: 'company/holiday-plan', element: <CompanyWorkspaceGuard feature="feature_holiday_plan"><HolidayPlanPage/></CompanyWorkspaceGuard> },
      { path: 'clockings', element: <CompanyWorkspaceGuard clockings><ClockingsPage/></CompanyWorkspaceGuard> },
      { path: 'config/holiday-plan', element: <CompanyWorkspaceGuard feature="feature_holiday_plan"><HolidayPlanConfigPage/></CompanyWorkspaceGuard> },
      { path: 'config/projects', element: <CompanyWorkspaceGuard><ProjectsConfigPage/></CompanyWorkspaceGuard> },
      { path: 'company/documents', element: <DocumentsPage/> },
      { path: 'company/stats', element: <StatistichePage/> },
      { path: 'company/timetable', element: <ScadenzarioPage/> },
      { path: 'auto', element: <AutoPage/> },
      { path: 'company/note-spese', element: <ExpenseReportsPage/> },
      { path: 'company/payroll-data', element: <CompanyPayrollDataPage/> },
      { path: 'config', element: <CompanyConfigPage/> },
      { path: 'config/timesheet', element: <TimesheetConfigPage/> },
      { path: 'config/clocking-places', element: <CompanyClockingPlacesPage/> },
      { path: 'config/expense-reports', element: <ExpenseReportsConfigPage/> },
      { path: 'config/supervisors', element: <SupervisorsConfigPage/> },
      { path: 'config/document-templates', element: <DocumentTemplatesPage/> },
      { path: 'config/custom-fields', element: <EmployeeCustomFieldsConfigPage/> },
      { path: 'config/status-options', element: <Navigate to={paths.company.config.customFields} replace/> },
      { path: 'config/areas', element: <AreasConfigPage/> },
      { path: 'company/account', element: <CompanyAccountPage/> },
      { path: 'integrazioni/pagheweb', element: <ZucchettiApiPage/> },
      { path: 'integrazioni/pagheweb/:tab', element: <ZucchettiApiPage/> },
      { path: 'integrazioni/bpoint', element: <BPointPage/> },
      { path: 'integrazioni/bpoint/:tab', element: <BPointPage/> },
      { path: 'integrazioni/zucchetti-export', element: <ZucchettiExportPage/> },
      { path: 'integrazioni/zucchetti-anteprima', element: <ZucchettiPreviewPage/> },
      { path: 'integrazioni/api-keys', element: <ApiKeysPage/> },
      { path: 'integrazioni/connettore-timbrature', element: <ClockingConnectorPage/> },
    ]
  }
];
